import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetManutenzione,
  useCreateTicketManutenzione,
  useUpdateTicketManutenzione,
  useGetCollaboratori,
  useGetStrutture,
  useGetCamere,
} from "@/lib/supabaseQueries";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle } from "lucide-react";

const STATI = ["Aperto", "In lavorazione", "Risolto", "Chiuso"];
const PRIORITA = ["Bassa", "Media", "Alta", "Urgente"];
const CATEGORIE = ["Impianti", "Arredamento", "Elettrodomestici", "Idraulica", "Elettricità", "Strutturale", "Pulizia", "Altro"];

const PRIORITA_COLOR: Record<string, { color: string; bg: string }> = {
  "Bassa": { color: "#22c55e", bg: "#f0fdf4" },
  "Media": { color: "#3b82f6", bg: "#eff6ff" },
  "Alta": { color: "#d97706", bg: "#fef3c7" },
  "Urgente": { color: "#ef4444", bg: "#fef2f2" },
};

const emptyForm = {
  struttura_id: 0, struttura_nome: "",
  camera_id: 0, camera_nome: "",
  titolo: "", descrizione: "",
  categoria: "Altro", priorita: "Media", stato: "Aperto",
  assegnato_id: 0, assegnato_nome: "", note: "",
};

export default function ManutenzioneTicketPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: allTickets = [], isLoading } = useGetManutenzione({}, { query: { enabled: !isNew, queryKey: ['manutenzione'] } });
  const existing = isNew ? null : (allTickets as any[]).find((t: any) => t.id === Number(id));
  const { data: collaboratori = [] } = useGetCollaboratori();
  const { data: strutture = [] } = useGetStrutture();
  const { data: camere = [] } = useGetCamere({});
  const create = useCreateTicketManutenzione();
  const update = useUpdateTicketManutenzione();

  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (existing && !isNew) {
      const ex = existing as any;
      setForm({
        struttura_id: ex.struttura_id ?? 0,
        struttura_nome: ex.struttura_nome ?? "",
        camera_id: ex.camera_id ?? 0,
        camera_nome: ex.camera_nome ?? "",
        titolo: ex.titolo ?? "",
        descrizione: ex.descrizione ?? "",
        categoria: ex.categoria ?? "Altro",
        priorita: ex.priorita ?? "Media",
        stato: ex.stato ?? "Aperto",
        assegnato_id: ex.assegnato_id ?? 0,
        assegnato_nome: ex.assegnato_nome ?? "",
        note: ex.note ?? "",
      });
    }
  }, [existing, isNew]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const manutentori = (collaboratori as any[]).filter(c => {
    const r = (() => { try { return JSON.parse(c.ruoli || "[]"); } catch { return []; } })();
    return c.attivo && (r.includes("Manutenzione") || c.ruolo === "Manutenzione");
  });

  const cameraOptions = (camere as any[]).filter(c => !form.struttura_id || c.struttura_id === form.struttura_id);

  const handleSave = () => {
    if (!form.titolo) return;
    setSaveError(null);
    const clean = {
      ...form,
      struttura_id: form.struttura_id || undefined,
      camera_id: form.camera_id || undefined,
      assegnato_id: form.assegnato_id || undefined,
    };
    const opts = {
      onSuccess: (res: any) => {
        qc.invalidateQueries({ queryKey: ["manutenzione"] });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (isNew && res?.id) navigate(`/team/manutenzione/${res.id}`, { replace: true });
      },
      onError: (err: any) => setSaveError(err?.message ?? "Errore durante il salvataggio"),
    };
    if (isNew) create.mutate(clean, opts);
    else update.mutate({ id: Number(id), data: clean }, opts as any);
  };

  const inp = "w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";
  const lbl = "block text-sm font-medium text-foreground mb-1.5";
  const isPending = create.isPending || update.isPending;
  const priCfg = PRIORITA_COLOR[form.priorita] ?? PRIORITA_COLOR["Media"];

  if (!isNew && isLoading) return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/team/manutenzione")}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-border hover:border-primary hover:text-primary transition-colors text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">{isNew ? "Nuovo ticket" : form.titolo || "Modifica ticket"}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {!isNew && (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ color: priCfg.color, backgroundColor: priCfg.bg }}>
                  {form.priorita}
                </span>
                · {form.stato}
              </span>
            )}
            {isNew && "Segnala un problema o un intervento da fare"}
          </p>
        </div>
      </div>

      {saveError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{saveError}</div>
      )}

      <div className="space-y-4">
        {/* Titolo e Descrizione */}
        <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Dettaglio</h3>
          <div>
            <label className={lbl}>Titolo *</label>
            <input className={inp} value={form.titolo} onChange={e => set("titolo", e.target.value)} placeholder="Es. Lavatrice non funziona" />
          </div>
          <div>
            <label className={lbl}>Descrizione</label>
            <textarea className={inp + " resize-none"} rows={4} value={form.descrizione}
              onChange={e => set("descrizione", e.target.value)} placeholder="Descrivi il problema nel dettaglio..." />
          </div>
        </div>

        {/* Classificazione */}
        <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Classificazione</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Categoria</label>
              <select className={inp} value={form.categoria} onChange={e => set("categoria", e.target.value)}>
                {CATEGORIE.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Priorità</label>
              <select className={inp} value={form.priorita} onChange={e => set("priorita", e.target.value)}>
                {PRIORITA.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          {!isNew && (
            <div>
              <label className={lbl}>Stato</label>
              <div className="grid grid-cols-2 gap-2">
                {STATI.map(s => (
                  <button key={s} type="button" onClick={() => set("stato", s)}
                    className="py-2.5 rounded-xl text-sm font-medium border transition-all"
                    style={{
                      borderColor: form.stato === s ? "#1a1814" : "#e8e3dc",
                      backgroundColor: form.stato === s ? "#1a1814" : "white",
                      color: form.stato === s ? "white" : "#7a756e",
                    }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Posizione */}
        <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Posizione</h3>
          <div>
            <label className={lbl}>Struttura</label>
            <select className={inp} value={form.struttura_id} onChange={e => {
              const sid = Number(e.target.value);
              const s = (strutture as any[]).find(x => x.id === sid);
              set("struttura_id", sid); set("struttura_nome", s?.nome ?? "");
              set("camera_id", 0); set("camera_nome", "");
            }}>
              <option value={0}>— Seleziona struttura —</option>
              {(strutture as any[]).map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          {cameraOptions.length > 0 && (
            <div>
              <label className={lbl}>Camera (opzionale)</label>
              <select className={inp} value={form.camera_id} onChange={e => {
                const cid = Number(e.target.value);
                const c = cameraOptions.find((x: any) => x.id === cid);
                set("camera_id", cid); set("camera_nome", (c as any)?.nome ?? "");
              }}>
                <option value={0}>— Tutte le aree —</option>
                {cameraOptions.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Assegnazione */}
        <div className="bg-card rounded-2xl border border-border p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Assegna a</h3>
          {manutentori.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun collaboratore con ruolo Manutenzione</p>
          ) : (
            <div className="space-y-2">
              <div onClick={() => { set("assegnato_id", 0); set("assegnato_nome", ""); }}
                className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all"
                style={{ borderColor: !form.assegnato_id ? "#1a1814" : "#e8e3dc", backgroundColor: !form.assegnato_id ? "#f9f7f4" : "white" }}>
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-sm">—</div>
                <span className="text-sm text-muted-foreground">Non assegnato</span>
              </div>
              {manutentori.map((c: any) => {
                const sel = form.assegnato_id === c.id;
                return (
                  <div key={c.id} onClick={() => { set("assegnato_id", c.id); set("assegnato_nome", `${c.nome} ${c.cognome ?? ""}`.trim()); }}
                    className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all"
                    style={{ borderColor: sel ? "#c87045" : "#e8e3dc", backgroundColor: sel ? "#fdf6f1" : "white" }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 bg-primary">
                      {c.nome[0]}
                    </div>
                    <span className="text-sm font-medium text-foreground">{c.nome} {c.cognome}</span>
                    {sel && <CheckCircle className="w-4 h-4 ml-auto flex-shrink-0 text-primary" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="bg-card rounded-2xl border border-border p-5">
          <label className="block text-sm font-semibold text-foreground mb-2">Note aggiuntive</label>
          <textarea className={inp + " resize-none"} rows={3} value={form.note}
            onChange={e => set("note", e.target.value)} placeholder="Informazioni aggiuntive o istruzioni..." />
        </div>

        {/* Save */}
        <div className="pb-8">
          <button onClick={handleSave} disabled={isPending || !form.titolo}
            className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {saved ? <><CheckCircle className="w-4 h-4" /> Salvato!</> : isPending ? "Salvataggio..." : "Salva ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}
